import { useEffect, useState } from "react";
import { IDropdownOption } from "@fluentui/react";
import { fetchAnalyticalWorkspaces } from "../../../api/object/AnalyticalWorkspace";
import { descriptionLanguage } from "../../../utils/helpers/LanguageHelper";
import { fetchDatasets } from "../../../api/object/Dataset";
import { TgkAmountObjectSource } from "../../interfaces/CoObject";
import { DatasetField } from "../../../api/models/dataset-field";
import { extractAmountsFromDatasetFields } from "../../../api/object/DatasetField";

export interface DatasetAmountDropdownOption extends IDropdownOption {
  partitionType: string;
}

const useCoObjectSourceAmountAnalyticalPanel = (props) => {
  const { objectSource, isComponentInstance, isLocked, validateSource, updateSource, datasetFields } = props;

  const [analyticalWorkspaceOptions, setAnalyticalWorkspaceOptions] = useState<IDropdownOption[]>([]);
  const [datasetOptions, setDatasetOptions] = useState<DatasetAmountDropdownOption[]>([]);
  const [amountOptions, setAmountOptions] = useState<IDropdownOption[]>([]);

  useEffect(() => {
    const fetchAnalyticalWorkspaces = async () => {
      if (objectSource?.dbApplication) {
        try {
          const analyticalWorkspaceOptions = await getAnalyticalWorkspaceOptions(
            objectSource.dbApplication.id.toString()
          );
          setAnalyticalWorkspaceOptions(analyticalWorkspaceOptions);
        } catch (e) {
          // eslint-disable-next-line no-undef
          console.error(e);
        }
      } else {
        setAnalyticalWorkspaceOptions([]);
      }
    };
    if (objectSource?.dbApplication) {
      fetchAnalyticalWorkspaces();
    }
  }, [objectSource?.dbApplication]);

  useEffect(() => {
    const fetchDatasets = async () => {
      if (objectSource?.analyticalWorkspace?.code) {
        try {
          const datasetOptions = await getDatasetOptions(
            objectSource.dbApplication.id.toString(),
            objectSource.analyticalWorkspace.code.toString()
          );
          setDatasetOptions(datasetOptions);
        } catch (e) {
          // eslint-disable-next-line no-undef
          console.error(e);
        }
      } else {
        setDatasetOptions([]);
      }
    };
    fetchDatasets();
  }, [objectSource?.analyticalWorkspace?.code, analyticalWorkspaceOptions]);

  useEffect(() => {
    const fetchAmounts = async () => {
      if (objectSource?.dataset?.code) {
        try {
          const amount = datasetFields ? extractAmountsFromDatasetFields(datasetFields) : [];
          setAmountOptions(getDatasetFieldOptions(amount));
        } catch (error) {
          // eslint-disable-next-line no-undef
          console.error("Error fetching amount:", error);
        }
      } else {
        setAmountOptions([]);
      }
    };
    fetchAmounts();
  }, [objectSource?.dataset?.code, datasetFields, datasetOptions]);

  async function getAnalyticalWorkspaceOptions(dbId: string): Promise<IDropdownOption[]> {
    const analyticalWorkspaces = await fetchAnalyticalWorkspaces(dbId, descriptionLanguage());
    return analyticalWorkspaces.map((analyticalWorkspace) => ({
      key: analyticalWorkspace.code,
      text: analyticalWorkspace.description,
    }));
  }

  async function getDatasetOptions(dbId: string, awCode: string): Promise<DatasetAmountDropdownOption[]> {
    const datasets = await fetchDatasets(dbId, awCode, descriptionLanguage());
    return datasets.map((dataset) => ({
      key: dataset.code,
      text: dataset.description,
      partitionType: dataset.partitionType,
    }));
  }

  function getDatasetFieldOptions(datasetFields: DatasetField[]): IDropdownOption[] {
    if (!datasetFields || datasetFields.length === 0) {
      return [];
    }

    return datasetFields.map((field) => ({
      key: field.id,
      text: field.description,
    }));
  }

  async function handleOnChangeAnalyticalWorkspace(selectedAnalyticalWorkspace: IDropdownOption | undefined) {
    if (selectedAnalyticalWorkspace?.key !== objectSource?.analyticalWorkspace?.code) {
      const obj: TgkAmountObjectSource = {
        ...objectSource,
        type: "AMOUNT_AIH",
        dbApplication: objectSource.dbApplication,
        analyticalWorkspace: {
          code: selectedAnalyticalWorkspace.key.toString(),
          description: selectedAnalyticalWorkspace.text,
        },
        dataset: null,
        amount: null,
        dimensions: [],
      };
      updateSource(obj, validateSource(obj), true);
    }
  }

  async function handleOnChangeDataset(selectedDataset: DatasetAmountDropdownOption | undefined) {
    if (selectedDataset?.key !== objectSource?.dataset?.code) {
      const obj: TgkAmountObjectSource = {
        ...objectSource,
        type: "AMOUNT_AIH",
        dbApplication: objectSource.dbApplication,
        analyticalWorkspace: objectSource.analyticalWorkspace,
        dataset: {
          code: selectedDataset.key.toString(),
          description: selectedDataset.text,
          partitionType: selectedDataset.partitionType,
        },
        amount: null,
        dimensions: [],
      };
      updateSource(obj, validateSource(obj), true);
    }
  }

  async function handleOnChangeAmount(selectedDatasetField: IDropdownOption | undefined) {
    if (selectedDatasetField?.key !== objectSource?.amount?.id) {
      const obj: TgkAmountObjectSource = {
        ...objectSource,
        type: "AMOUNT_AIH",
        dbApplication: objectSource.dbApplication,
        analyticalWorkspace: objectSource.analyticalWorkspace,
        dataset: objectSource.dataset,
        amount: {
          id: selectedDatasetField.key.toString(),
          description: selectedDatasetField.text,
        },
        dimensions: [],
      };
      updateSource(obj, validateSource(obj), true);
    }
  }
  return {
    objectSource,
    amountOptions,
    datasetOptions,
    analyticalWorkspaceOptions,
    isComponentInstance,
    isLocked,
    handleOnChangeAnalyticalWorkspace,
    handleOnChangeDataset,
    handleOnChangeAmount,
  };
};
export default useCoObjectSourceAmountAnalyticalPanel;
